import { gray } from '@kizen/kds/Colors';
import { transform, TransformCtx } from './parser';
import { MJ_TEXT_CLASS_NAME } from './styles';
import {
  BuilderJson,
  MjmlAttributeChild,
  MjmlBody,
  MjmlHead,
  MjmlHeadChild,
  MjmlRoot,
  MjmlRootAttributes,
  MjmlStyle,
  MjmlText,
  WithoutChildren,
} from './types';

const LIGHT = gray['gray-300'];
const MEDIUM = gray['gray-500'];
const DARK = gray['gray-800'];

export const createDefaultTextStyle = (): [
  MjmlStyle,
  WithoutChildren<MjmlText>,
] => {
  return [
    {
      tagName: 'mj-style',
      // We want to mimic the styles setup for the text node in the builder:
      // apps/react-app/src/components/PageBuilder/Editor/nodes/Text.js
      // Notes (don't put comments in css):
      // - min-height: 1em on paragraphs ensures empty paragraphs do not collapse to 0px height in Gmail
      content: `
          .${MJ_TEXT_CLASS_NAME} p {
            margin: 0;
            line-height: 1.5em;
            min-height: 1em;
          }
          .${MJ_TEXT_CLASS_NAME} ul, ol {
            margin-top: 0;
            margin-bottom: 10px;
          }
          .${MJ_TEXT_CLASS_NAME} p * {
            font-family: inherit;
            font-size: inherit;
            line-height: inherit;
          }
          .${MJ_TEXT_CLASS_NAME} code {
            font-family: 'Courier New', Monospace;
            font-size: inherit;
            font-weight: 400;
            background-color: ${LIGHT};
            padding: 5px;
            color: ${DARK};
            border-radius: 4px;
          }
          .${MJ_TEXT_CLASS_NAME} pre {
            padding: 10px;
            background-color: ${LIGHT};
            border-radius: 4px;
            border: 1px solid ${MEDIUM};
          }
          .${MJ_TEXT_CLASS_NAME} pre code {
              padding: 0;
              background-color: unset;
              border-radius: 0;
              white-space: pre-wrap;
              word-break: break-all;
            }
          }
        `,
    },
    {
      tagName: 'mj-text',
      attributes: {
        'css-class': MJ_TEXT_CLASS_NAME,
        'font-size': '14px',
      },
    },
  ];
};

type WithRootNodeOpts = {
  attributes?: MjmlRootAttributes;
  headAttributes?: MjmlAttributeChild[];
  headChildren?: MjmlHeadChild[];
};

const buildHeadNode = ({
  attributes,
  children = [],
}: {
  children?: MjmlHeadChild[];
  attributes?: MjmlAttributeChild[];
}): MjmlHead => ({
  tagName: 'mj-head',
  children: attributes
    ? children.concat({ tagName: 'mj-attributes', children: attributes })
    : children,
});

export const convertBuilderJson = (
  allNodes: BuilderJson,
  {
    processDynamicImages,
    processDynamicText,
  }: Pick<TransformCtx, 'processDynamicImages' | 'processDynamicText'> = {}
): [
  MjmlBody,
  {
    headAttributes: MjmlAttributeChild[];
    headChildren: TransformCtx['headChildren'];
  },
] => {
  const headAttributes: MjmlAttributeChild[] = [];
  const headChildren: TransformCtx['headChildren'] = [];
  const rootNode = { ...allNodes.ROOT, id: 'ROOT' };
  const body = transform(rootNode, {
    allNodes,
    headAttributes,
    headChildren,
    processDynamicImages,
    processDynamicText,
    globalTextLinkStyleAdded: false,
  }) as MjmlBody;
  return [body, { headAttributes, headChildren }];
};

export const withRootNode = (
  body: MjmlBody,
  { attributes = {}, headChildren, headAttributes }: WithRootNodeOpts
): MjmlRoot => ({
  tagName: 'mjml',
  attributes,
  children:
    headAttributes || headChildren
      ? [
          buildHeadNode({ children: headChildren, attributes: headAttributes }),
          body,
        ]
      : [body],
});
